/**
 * Password Recovery Modal for @ollaid/native-sso
 * Flow: email → method-choice → OTP → new password → success
 * Design aligned with web SSO
 * 
 * @version 2.7.9
 */

import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter,
  Button, Input, Label, RadioGroup, RadioGroupItem,
  IconLock, IconCheckCircle2, IconArrowLeft, IconKeyRound, IconLoader2,
  BrandLinkRow,
} from './ui';
import OTPInput from './OTPInput';
import { useMobilePassword } from '../hooks/useMobilePassword';
import packageFavicon from '../logo/favicon.png';

export interface PasswordRecoveryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  saasApiUrl: string;
  iamApiUrl: string;
  
}

type RecoveryStep = 'email' | 'method-choice' | 'otp' | 'password' | 'success';

const C = {
  primary: '#002147',
  green: '#16a34a',
  greenBg: '#dcfce7',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray500: '#6b7280',
  gray700: '#374151',
  gray900: '#111827',
  red: '#dc2626',
  redBg: '#fef2f2',
  white: '#ffffff',
} as const;

const iconCircle = (bg: string, size = '3rem'): React.CSSProperties => ({
  margin: '0 auto', width: size, height: size, backgroundColor: bg, borderRadius: '50%',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
});

const backBtnStyle: React.CSSProperties = {
  position: 'absolute', left: '1rem', top: '1rem', background: 'none', border: 'none',
  cursor: 'pointer', padding: '0.5rem', borderRadius: '0.375rem', color: C.gray700, zIndex: 10,
};

export function PasswordRecoveryModal({ open, onOpenChange, onSuccess, saasApiUrl, iamApiUrl }: PasswordRecoveryModalProps) {
  const {
    status, loading: pwLoading, error: pwError, receiveMode, maskedEmail, maskedPhone,
    initRecovery, selectMethod, verifyOtp, resetPassword, resendOtp, reset: resetHook, clearError,
  } = useMobilePassword({ saasApiUrl, iamApiUrl });

  const [step, setStep] = useState<RecoveryStep>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<'email' | 'phone'>('email');
  const [resendCooldown, setResendCooldown] = useState(0);

  const error = localError || pwError;

  useEffect(() => {
    if (status === 'choice_required') setStep('method-choice');
    else if (status === 'pending_otp') { setStep('otp'); setResendCooldown(60); }
    else if (status === 'pending_password') setStep('password');
    else if (status === 'completed') setStep('success');
  }, [status]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const resetState = () => {
    setStep('email'); setEmail(''); setOtp(''); setPassword(''); setConfirmPassword('');
    setLocalError(null); setSelectedMethod('email'); setResendCooldown(0); resetHook();
  };

  const handleClose = () => { resetState(); onOpenChange(false); };
  const handleBackToLogin = () => { resetState(); onSuccess(); };

  const handleEmailSubmit = async () => {
    setLocalError(null); clearError();
    if (!email.trim()) { setLocalError("L'adresse email est requise"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setLocalError('Veuillez entrer une adresse email valide'); return; }
    await initRecovery(email.trim());
  };

  const handleMethodSubmit = async () => {
    setLocalError(null); clearError();
    await selectMethod(selectedMethod);
  };

  const handleOTPSubmit = () => {
    if (otp.length !== 6) { setLocalError('Veuillez entrer le code à 6 chiffres'); return; }
    setLocalError(null); clearError();
    const isValid = verifyOtp(otp);
    if (!isValid) setLocalError('Code incorrect');
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    const result = await resendOtp();
    if (result.success) { setResendCooldown(result.cooldown || 60); setOtp(''); setLocalError(null); }
  };

  const handlePasswordSubmit = async () => {
    if (password.length < 8) { setLocalError('Le mot de passe doit contenir au moins 8 caractères'); return; }
    if (password !== confirmPassword) { setLocalError('Les mots de passe ne correspondent pas'); return; }
    setLocalError(null); clearError();
    await resetPassword(password);
  };

  const goBack = () => {
    setLocalError(null); clearError();
    if (step === 'method-choice') { setStep('email'); resetHook(); }
    else if (step === 'otp') { setStep(maskedPhone ? 'method-choice' : 'email'); resetHook(); }
    else if (step === 'password') setStep('otp');
  };

  // ===== SUCCESS =====
  if (step === 'success') {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent>
          <DialogHeader style={{ textAlign: 'center' }}>
            <div style={{ ...iconCircle(C.greenBg), width: '5rem', height: '5rem' }}>
              <IconCheckCircle2 style={{ width: '3rem', height: '3rem', color: C.green }} />
            </div>
            <DialogTitle>Mot de passe modifié !</DialogTitle>
            <DialogDescription>Votre mot de passe a été changé avec succès.</DialogDescription>
          </DialogHeader>
          <DialogFooter style={{ borderTop: 'none' }}>
            <Button onClick={handleBackToLogin} style={{ width: '100%' }}>Retour à la connexion</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        {step !== 'email' && (
          <button onClick={goBack} style={backBtnStyle} type="button">
            <IconArrowLeft style={{ width: '1rem', height: '1rem' }} />
          </button>
        )}

        <DialogHeader style={{ textAlign: 'center' }}>
          <BrandLinkRow
            icon={step === 'otp' ? <IconKeyRound style={{ width: '1.25rem', height: '1.25rem', color: C.primary }} /> : <IconLock style={{ width: '1.25rem', height: '1.25rem', color: C.primary }} />}
            faviconSrc={packageFavicon}
            faviconAlt="Ollaid SSO"
            iconBg={C.primary + '1a'}
            iconColor={C.primary}
          />
          <DialogTitle>
            {step === 'email' && 'Récupération de mot de passe'}
            {step === 'method-choice' && 'Recevoir le code de vérification'}
            {step === 'otp' && 'Vérification'}
            {step === 'password' && 'Nouveau mot de passe'}
          </DialogTitle>
          <DialogDescription>
            {step === 'email' && 'Veuillez entrer votre adresse email'}
            {step === 'method-choice' && 'Comment souhaitez-vous recevoir le code ?'}
            {step === 'otp' && (receiveMode === 'phone' ? `Code envoyé par SMS au ${maskedPhone}` : `Code envoyé par email à ${maskedEmail}`)}
            {step === 'password' && 'Définissez votre nouveau mot de passe'}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {error && <div style={{ padding: '0.75rem', borderRadius: '0.375rem', backgroundColor: C.redBg, color: C.red, fontSize: '0.875rem' }}>{error}</div>}

            {step === 'email' && (
              <div><Label htmlFor="recovery-email">Adresse email</Label><Input id="recovery-email" type="email" placeholder="vous@exemple.com" value={email} onChange={e => setEmail(e.target.value)} disabled={pwLoading} /></div>
            )}

            {step === 'method-choice' && (
              <RadioGroup value={selectedMethod} onValueChange={v => setSelectedMethod(v as 'email' | 'phone')}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem', borderRadius: '0.5rem', border: `2px solid ${selectedMethod === 'email' ? C.primary : C.gray200}`, cursor: 'pointer' }} onClick={() => setSelectedMethod('email')}>
                    <RadioGroupItem value="email" id="method-email" />
                    <div><div style={{ fontWeight: 500 }}>Par email</div><div style={{ fontSize: '0.75rem', color: C.gray500 }}>{maskedEmail}</div></div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem', borderRadius: '0.5rem', border: `2px solid ${selectedMethod === 'phone' ? C.primary : C.gray200}`, cursor: 'pointer' }} onClick={() => setSelectedMethod('phone')}>
                    <RadioGroupItem value="phone" id="method-phone" />
                    <div><div style={{ fontWeight: 500 }}>Par SMS</div><div style={{ fontSize: '0.75rem', color: C.gray500 }}>{maskedPhone}</div></div>
                  </div>
                </div>
              </RadioGroup>
            )}

            {step === 'otp' && (
              <>
                <OTPInput value={otp} onChange={setOtp} disabled={pwLoading} />
                <div style={{ textAlign: 'center', fontSize: '0.875rem' }}>
                  {resendCooldown > 0 ? <span style={{ color: C.gray500 }}>Renvoyer dans {resendCooldown}s</span> : (
                    <button type="button" style={{ color: C.primary, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }} onClick={handleResendOTP}>Code non reçu ? Renvoyer</button>
                  )}
                </div>
              </>
            )}

            {step === 'password' && (
              <>
                <div><Label>Nouveau mot de passe</Label><Input type="password" placeholder="Minimum 8 caractères" value={password} onChange={e => setPassword(e.target.value)} disabled={pwLoading} /></div>
                <div><Label>Confirmer le mot de passe</Label><Input type="password" placeholder="Retapez votre mot de passe" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} disabled={pwLoading} /></div>
              </>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          {step === 'email' && (
            <>
              <Button onClick={handleEmailSubmit} disabled={pwLoading} style={{ width: '100%' }}>
                {pwLoading ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <IconLoader2 style={{ width: '1rem', height: '1rem' }} /> Vérification...
                  </span>
                ) : 'Continuer'}
              </Button>
              <Button variant="ghost" onClick={handleBackToLogin} style={{ width: '100%' }}>Retour à la connexion</Button>
            </>
          )}
          {step === 'method-choice' && (
            <Button onClick={handleMethodSubmit} disabled={pwLoading} style={{ width: '100%' }}>
              {pwLoading ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <IconLoader2 style={{ width: '1rem', height: '1rem' }} /> Envoi...
                </span>
              ) : 'Envoyer le code'}
            </Button>
          )}
          {step === 'otp' && (
            <Button onClick={handleOTPSubmit} disabled={otp.length !== 6} style={{ width: '100%' }}>Vérifier</Button>
          )}
          {step === 'password' && (
            <Button onClick={handlePasswordSubmit} disabled={pwLoading || !password || !confirmPassword} style={{ width: '100%' }}>
              {pwLoading ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <IconLoader2 style={{ width: '1rem', height: '1rem' }} /> Modification...
                </span>
              ) : 'Modifier le mot de passe'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PasswordRecoveryModal;
